// The add-in's "open in the browser" lands here with a single-use code: trade it for a session, go to `next`.
// A code already used or expired falls back on the browser's own session, else on the sign-in page.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { Alert, Button, Spinner } from '../components/ui';
import { ApiError, api } from '../services/api';
import { parseLinkHash } from '../services/link';
import { hasSession } from '../services/tokenStore';
import { useSession } from '../session';

export function LinkPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reload } = useSession();
  const [failedNext, setFailedNext] = useState<string | null>(null);
  const [limited, setLimited] = useState(false);
  const started = useRef(false); // StrictMode runs the effect twice; the code can be traded once

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const { code, next } = parseLinkHash(window.location.hash);
    try {
      window.history.replaceState(null, '', window.location.pathname); // the code is spent: keep it out of the history
    } catch {
      /* ignore */
    }
    const go = async () => {
      let ok = false;
      if (code) {
        try {
          await api.exchangeLink(code);
          ok = true;
        } catch (err) {
          ok = false;
          if (err instanceof ApiError && err.is('rate_limited')) setLimited(true);
        }
      }
      if (ok || hasSession()) {
        await reload();
        navigate(next, { replace: true });
      } else {
        setFailedNext(next);
      }
    };
    void go();
  }, [navigate, reload]);

  return (
    <PublicLayout title={failedNext ? (limited ? t('app.rateLimitedTitle') : t('link.failed')) : t('callback.title')}>
      {failedNext ? (
        <>
          <Alert kind="warn">{limited ? t('app.rateLimited') : t('link.expired')}</Alert>
          <Button className="w-full" onClick={() => navigate('/login', { replace: true, state: { returnTo: failedNext } })}>
            {t('link.signIn')}
          </Button>
        </>
      ) : (
        <Spinner />
      )}
    </PublicLayout>
  );
}
